import { Box, Typography } from '@mui/material';
import {
  ALT_DESCRIPTORS_PENDING,
  altDomainStatuses,
  altLevelIndex,
  altNextPhrase,
  altNowPhrase,
  altSeries,
  type AltDomainStatus,
  type AltSeriesPoint,
} from './altPlanContent';
import { altLevelLabel, DOMAIN_LABELS, formatSchoolYear, type PlanStudent } from './derive';
import { CARD_SHADOW, COLORS, TREND_STYLES } from './tokens';

const INK3 = '#7F7F99';

export const ALT_DOMAIN_PROFILE_FOOTNOTE =
  'Source: descriptors will be drawn from the WIDA Alternate Proficiency Level Descriptors + Alternate Can Do Descriptors. Per-domain recommendations and accommodations are deferred for Alternate ACCESS — see Focus by Domain.';

function MiniTrajectory({ series, domain }: { series: AltSeriesPoint[]; domain: string }) {
  if (series.length === 0) return null;
  const scales = series.map((p) => p.scale);
  const lo = Math.min(...scales);
  const hi = Math.max(...scales);
  const yFor = (scale: number): number =>
    hi === lo ? 65 : 100 - ((scale - lo) / (hi - lo)) * 70;
  const first = series[0]!;
  const last = series[series.length - 1]!;
  const span = last.year - first.year;
  const xFor = (i: number): number => (span === 0 ? 230 : 30 + ((series[i]!.year - first.year) / span) * 400);
  return (
    <svg
      viewBox="0 -10 460 160"
      width="100%"
      role="img"
      aria-label={`${domain} Alternate ACCESS scale score trajectory`}
    >
      <line x1={30} y1={112} x2={430} y2={112} stroke={COLORS.divider} strokeWidth={1} />
      {series.length > 1 && (
        <path
          d={series.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(p.scale).toFixed(1)}`).join(' ')}
          fill="none"
          stroke={COLORS.navy}
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}
      {series.map((p, i) => {
        const end = i === series.length - 1;
        return (
          <g key={p.year}>
            <text x={xFor(i)} y={yFor(p.scale) - 9} fontSize={end ? 12 : 11} fontWeight={end ? 800 : 500} fill={end ? COLORS.ink : COLORS.muted} textAnchor="middle">
              {p.scale}
            </text>
            {end ? (
              <circle cx={xFor(i)} cy={yFor(p.scale)} r={6} fill={COLORS.navy} />
            ) : (
              <circle cx={xFor(i)} cy={yFor(p.scale)} r={5} fill="#fff" stroke={COLORS.navy} strokeWidth={2} />
            )}
            <text x={xFor(i)} y={132} fontSize={11} fontWeight={end ? 700 : 500} fill={end ? COLORS.navy : COLORS.muted} textAnchor="middle">
              {formatSchoolYear(p.year)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function DomainCard({ status, student }: { status: AltDomainStatus; student: PlanStudent }) {
  const name = DOMAIN_LABELS[status.domain];
  const scoreMeta =
    status.level != null
      ? `Level ${altLevelIndex(status.level)} · ${status.label}${
          status.delta != null
            ? status.delta > 0
              ? ` · +${status.delta} vs last year`
              : status.delta < 0
                ? ` · ${status.delta} vs last year`
                : ' · even with last year'
            : ''
        }`
      : 'No scored Alternate ACCESS administration';
  const nowPhrase = status.level != null ? altNowPhrase(status.domain, status.level) : null;
  const nextPhrase = status.level != null ? altNextPhrase(status.domain, status.level) : null;
  return (
    <Box sx={{ flex: '1 1 45%', minWidth: 280, bgcolor: COLORS.bg, borderRadius: '8px', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>{name}</Typography>
          {status.delta != null && status.delta !== 0 && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '9999px', bgcolor: status.delta > 0 ? TREND_STYLES.rising.bg : TREND_STYLES.falling.bg, color: COLORS.ink, fontSize: 12, fontWeight: 700 }}>
              <Box component="span" sx={{ fontSize: 10, color: status.delta > 0 ? TREND_STYLES.rising.fg : TREND_STYLES.falling.fg }}>
                {status.delta > 0 ? '▲' : '▼'}
              </Box>
              {status.delta > 0 ? 'Up' : 'Down'}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <Typography sx={{ fontSize: 32, lineHeight: '40px', fontWeight: 800, letterSpacing: '-0.5px', color: COLORS.ink }}>
            {status.scale ?? '—'}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>{scoreMeta}</Typography>
        </Box>
      </Box>
      {status.level != null && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>
            {`Alternate Can Do • Level ${altLevelIndex(status.level)} ${status.label}`}
          </Typography>
          {nowPhrase != null && (
            <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
              {nowPhrase}
            </Typography>
          )}
          {altLevelIndex(status.level) < 5 && nextPhrase != null && (
            <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: COLORS.muted }}>
              <Box component="b" sx={{ fontWeight: 700 }}>
                {`Next → ${altLevelLabel(altLevelIndex(status.level) + 1)}:`}
              </Box>{' '}
              {nextPhrase}
            </Typography>
          )}
        </Box>
      )}
      <MiniTrajectory series={altSeries(student, status.domain)} domain={name} />
    </Box>
  );
}

export function AltDomainProfile({ student }: { student: PlanStudent }) {
  const first = student.firstName || 'This student';
  return (
    <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: CARD_SHADOW, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 600, color: COLORS.ink }}>
            Domain Profile
          </Typography>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', bgcolor: COLORS.bg, color: COLORS.navy, fontSize: 12, fontWeight: 700 }}>
            Alternate ACCESS scale
          </Box>
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted }}>
          {`What ${first} can do now and what the next Alternate level looks like, per domain. Descriptive only — no prescriptive recommendations (deferred).`}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: INK3 }}>
          {ALT_DESCRIPTORS_PENDING}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {altDomainStatuses(student).map((status) => (
          <DomainCard key={status.domain} status={status} student={student} />
        ))}
      </Box>
      <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 500, color: COLORS.muted }}>
        {ALT_DOMAIN_PROFILE_FOOTNOTE}
      </Typography>
    </Box>
  );
}
